import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { formatEventTimeRange } from '../utils/dateFormat';
import { ArrowLeft, Calendar, MapPin, ExternalLink, Copy, Gift, Scan, Eye, CopyPlus, UserPlus, BookOpen } from 'lucide-react';
import { CompGuestSection } from '../components/CompGuestSection';
import { PendingApprovals } from '../components/manage-event/PendingApprovals';
import { AttendeeList } from '../components/manage-event/AttendeeList';
import { StaffPanel } from '../components/manage-event/StaffPanel';
import { AnalyticsPanel } from '../components/manage-event/AnalyticsPanel';
import { DiscountCodesPanel } from '../components/manage-event/DiscountCodesPanel';
import { MessagingPanel } from '../components/manage-event/MessagingPanel';
import ActivityFeedCard from '../components/activity/ActivityFeedCard';
import InsightsSnapshotCard from '../components/event/InsightsSnapshotCard';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManageEvent({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [approvingBulk, setApprovingBulk] = useState(false);
  const [showCompForm, setShowCompForm] = useState(false);
  const [compForm, setCompForm] = useState({ guest_name: '', guest_email: '', pass_tier_id: '', attendees_count: 1, comp_note: '' });
  const [issuingComp, setIssuingComp] = useState(false);
  const [staff, setStaff] = useState([]);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  const [compGuests, setCompGuests] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [replaceModal, setReplaceModal] = useState(null); // ticket object or null
  const [replacing, setReplacing] = useState(false);
  // Iter126: phase-aware tab state (Setup / Live / Wrap). Default tab is
  // computed from event phase once event data lands; hosts can manually
  // override at any time and the override persists for the session.
  const [activeTab, setActiveTab] = useState(null); // null until event loads

  const compGuestRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [eventRes, ticketsRes, availRes, staffRes, discountRes, msgRes, profilesRes, compGuestsRes] = await Promise.all([
        axios.get(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets?event_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/${id}/availability`),
        axios.get(`${API}/events/${id}/staff`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${id}/discount-codes`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${id}/messages`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/staff-profiles`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/comp-guests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data); setTickets(ticketsRes.data); setAvailability(availRes.data); setStaff(staffRes.data); setDiscountCodes(discountRes.data); setMessages(msgRes.data); setSavedProfiles(profilesRes.data); setCompGuests(compGuestsRes.data);
    } catch { toast.error('Failed to load'); navigate('/dashboard'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Ticket actions
  const handleRevokeTicket = async (ticketId) => { if (!confirm('Revoke this pass?')) return; try { const token = localStorage.getItem('session_token'); await axios.put(`${API}/tickets/${ticketId}/revoke`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Revoked'); fetchAll(); } catch { toast.error('Failed'); } };

  const handleConfirmPayment = async (ticket) => {
    if (!window.confirm(`Mark $${(ticket.amount_paid || 0).toFixed(2)} as collected for ${ticket.guest_display_name || ticket.guest_name}?`)) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/tickets/${ticket.ticket_id}/confirm-payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Payment collected');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark collected');
    }
  };

  const handleRevokeAndReplace = async () => {
    if (!replaceModal) return;
    setReplacing(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/tickets/${replaceModal.ticket_id}/revoke-and-replace`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.replacement === 'created') {
        toast.success(`Replacement issued: ${res.data.replacement_ticket_id}`);
      } else if (res.data.replacement === 'failed') {
        toast.error(`Revoke succeeded but replacement failed: ${res.data.detail}. You can retry.`);
      }
      setReplaceModal(null);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Revoke & Replace failed';
      toast.error(detail);
    } finally {
      setReplacing(false);
    }
  };
  const handleScanTicket = async (ticketId) => { try { const token = localStorage.getItem('session_token'); const r = await axios.put(`${API}/tickets/${ticketId}/scan`, { check_in_method: 'manual' }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(r.data.message); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const handleIssueRefund = async (ticketId, payload) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/tickets/${ticketId}/refund`, payload || {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Refund initiated');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    }
  };
  const handleBulkApprove = async () => {
    if (selectedTickets.length === 0) return;
    setApprovingBulk(true);
    try { const token = localStorage.getItem('session_token'); const r = await axios.post(`${API}/tickets/bulk-approve`, selectedTickets, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }); toast.success(`${r.data.approved_count} approved`); setSelectedTickets([]); fetchAll(); }
    catch { toast.error('Failed'); } finally { setApprovingBulk(false); }
  };
  const toggleTicketSelection = (ticketId) => setSelectedTickets(prev => prev.includes(ticketId) ? prev.filter(i => i !== ticketId) : [...prev, ticketId]);
  const toggleSelectAll = (list) => { const ids = list.map(t => t.ticket_id); setSelectedTickets(prev => prev.length === ids.length ? [] : ids); };

  // Comp
  const handleIssueComp = async (e) => {
    e.preventDefault();
    if (!compForm.guest_name || !compForm.guest_email) { toast.error('Name and email required'); return; }
    setIssuingComp(true);
    try { const token = localStorage.getItem('session_token'); await axios.post(`${API}/events/${id}/comp-entry`, { ...compForm, pass_tier_id: compForm.pass_tier_id || null, attendees_count: parseInt(compForm.attendees_count) || 1, comp_note: compForm.comp_note || null }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Comp issued'); setCompForm({ guest_name: '', guest_email: '', pass_tier_id: '', attendees_count: 1, comp_note: '' }); setShowCompForm(false); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setIssuingComp(false); }
  };

  // Duplicate
  const handleDuplicate = async () => {
    setDuplicating(true);
    try { const token = localStorage.getItem('session_token'); const res = await axios.post(`${API}/events/${id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Event duplicated'); navigate(`/events/${res.data.event_id}/edit`); }
    catch { toast.error('Failed to duplicate event'); } finally { setDuplicating(false); }
  };

  const copyEventLink = () => { navigator.clipboard.writeText(`${window.location.origin}/e/${id}`); toast.success('Copied'); };

  if (loading || !event) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

  // Derived data
  const pendingTickets = tickets.filter(t => t.status === 'pending_approval');
  // Iter131: separate stale/expired pending requests from actionable ones so
  // the Pending Approvals banner and the "Pending" stat tile reflect what the
  // host can still meaningfully act on. We compute event-ended once and reuse
  // it; AttendeeList still shows ALL pending rows (with REQUEST EXPIRED
  // styling on the stale ones) so history isn't hidden.
  const eventEnded = (() => {
    if (!event?.date) return false;
    const startTime = event.time || '00:00';
    const start = new Date(`${event.date}T${startTime}`);
    let end;
    if (event.end_time) {
      end = new Date(`${event.date}T${event.end_time}`);
      if (end < start) end.setDate(end.getDate() + 1);
    } else {
      end = new Date(start.getTime() + 6 * 60 * 60 * 1000);
    }
    return end < new Date();
  })();
  const actionablePendingTickets = eventEnded ? [] : pendingTickets;
  const nonRevokedTickets = tickets.filter(t => t.status !== 'revoked');
  const paidTickets = nonRevokedTickets.filter(t => !t.is_comp);
  const compTickets = nonRevokedTickets.filter(t => t.is_comp);
  const totalAttendees = nonRevokedTickets.reduce((s, t) => s + (t.attendees_count || 1), 0);
  const scannedTickets = tickets.filter(t => t.status === 'used');
  // Cash RSVP tickets that haven't been collected at the door are NOT revenue
  // yet — track them separately so the "Revenue" card reflects money in hand.
  const uncollectedCash = paidTickets.filter(t => t.payment_mode === 'cash' && t.payment_confirmed !== true);
  const collectedTickets = paidTickets.filter(t => !(t.payment_mode === 'cash' && t.payment_confirmed !== true));
  const paidRevenue = collectedTickets.reduce((s, t) => s + (t.amount_paid || 0), 0);
  // Pending door total pulls EXPECTED amount (amount_paid stays 0 until confirmed).
  const pendingDoorTotal = uncollectedCash.reduce((s, t) => s + (t.expected_amount || 0), 0);
  const hasPendingDoor = uncollectedCash.length > 0;
  const compValue = compTickets.length > 0 && availability ? (() => {
    let val = 0;
    compTickets.forEach(t => {
      const tier = availability.tiers?.find(tr => tr.id === t.pass_tier_id);
      if (tier) val += tier.price * (t.entry_quantity || 1);
      (t.purchased_add_ons || []).forEach(ao => val += (ao.price || 0) * (ao.quantity || 1));
    });
    return val;
  })() : 0;

  const eventStatus = (() => {
    if (!event?.date) return 'upcoming';
    const now = new Date();
    const eventDate = new Date(event.date + 'T' + (event.time || '00:00'));
    const endTime = event.end_time ? (event.end_time < event.time ? new Date(new Date(eventDate).setDate(eventDate.getDate() + 1)).setHours(...event.end_time.split(':').map(Number)) : new Date(event.date + 'T' + event.end_time)) : new Date(eventDate.getTime() + 6 * 60 * 60 * 1000);
    if (now < eventDate) return 'upcoming';
    if (now <= endTime) return 'live';
    return 'ended';
  })();

  // Iter126: derive default tab from event phase. Once the host manually
  // switches tab, `activeTab` becomes non-null and we honor that until
  // page refresh (session-scoped).
  const defaultTabForStatus = eventStatus === 'live' ? 'live' : eventStatus === 'ended' ? 'wrap' : 'setup';
  const currentTab = activeTab || defaultTabForStatus;

  // Iter126: operational "still to arrive" counter — for both capped and
  // unlimited events this gives a meaningful number. Capped events also
  // keep showing "Remaining" capacity. Unlimited events drop the
  // meaningless "Unlimited" remaining label and show this instead.
  const stillToArrive = Math.max(0, totalAttendees - scannedTickets.length);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* ─── HERO ─── */}
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-3 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1] text-xs" data-testid="back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Button>
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-[#EAE7E1] tracking-tight break-words">{event.title}</h1>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${eventStatus === 'live' ? 'bg-emerald-500/15 text-emerald-400 animate-pulse' : eventStatus === 'ended' ? 'bg-[#555]/15 text-[#777]' : 'bg-[#D4AF37]/15 text-[#D4AF37]'}`} data-testid="event-status-badge">{eventStatus}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#AFAFAF]">
                <div className="flex items-center"><Calendar className="w-4 h-4 mr-1.5 text-[#D4AF37]" />{formatEventTimeRange(event)}</div>
                <div className="flex items-center"><MapPin className="w-4 h-4 mr-1.5 text-[#D4AF37]" />{event.location}</div>
              </div>
            </div>
            <Button onClick={() => navigate(`/events/${id}/check-in`)} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm font-black text-base px-8 py-5 h-auto shadow-lg shadow-[#D4AF37]/10" data-testid="check-in-scanner-button"><Scan className="w-5 h-5 mr-2" />Check-In Scanner</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ─── LIVE METRICS ─── */}
        {/* Iter126: unlimited-capacity events drop the meaningless
            "Remaining: Unlimited" cell in favor of operational counters
            (Expected + Still To Arrive). Capped events keep "Remaining". */}
        {(() => {
          const isUnlimited = !!event.is_unlimited_capacity;
          const cells = isUnlimited
            ? [
                ['Checked In', scannedTickets.length, 'text-[#D4AF37]'],
                ['Expected', totalAttendees, 'text-[#EAE7E1]'],
                ['Still To Arrive', stillToArrive, eventStatus === 'live' ? 'text-[#F2D27A] text-3xl' : 'text-[#EAE7E1]'],
                ['Pending', actionablePendingTickets.length, actionablePendingTickets.length > 0 ? 'text-[#FFB800]' : 'text-[#777]'],
                ['Revenue', `$${paidRevenue.toFixed(2)}`, 'text-[#D4AF37]'],
                ...(hasPendingDoor ? [['Pending Door', `$${pendingDoorTotal.toFixed(2)}`, 'text-[#F2D27A]']] : []),
                ['Comp Value', `$${compValue.toFixed(2)}`, 'text-[#AFAFAF]'],
              ]
            : [
                ['Checked In', scannedTickets.length, 'text-[#D4AF37]'],
                ['Remaining', event.capacity - totalAttendees, eventStatus === 'live' ? 'text-[#F2D27A] text-3xl' : 'text-[#EAE7E1]'],
                ['Pending', actionablePendingTickets.length, actionablePendingTickets.length > 0 ? 'text-[#FFB800]' : 'text-[#777]'],
                ['Revenue', `$${paidRevenue.toFixed(2)}`, 'text-[#D4AF37]'],
                ...(hasPendingDoor ? [['Pending Door', `$${pendingDoorTotal.toFixed(2)}`, 'text-[#F2D27A]']] : []),
                ['Comp Value', `$${compValue.toFixed(2)}`, 'text-[#AFAFAF]'],
              ];
          // Grid: 3 cols mobile, fixed N cols on sm+. Static class map so
          // Tailwind JIT picks up every variant at build time.
          const colsClass = {
            4: 'sm:grid-cols-4',
            5: 'sm:grid-cols-5',
            6: 'sm:grid-cols-6',
            7: 'sm:grid-cols-7',
          }[cells.length] || 'sm:grid-cols-5';
          return (
            <div className={`grid grid-cols-3 ${colsClass} gap-2 mb-5`} data-testid="capacity-stats">
              {cells.map(([label, val, color]) => (
                <div key={label} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-3 sm:p-4" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="text-[10px] font-medium text-[#777] uppercase tracking-wide">{label}</div>
                  <div className={`mt-1 text-xl sm:text-2xl font-bold ${color}`}>{val}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ─── ACTION STRIP ─── */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="action-strip">
          <Button onClick={() => setShowCompForm(!showCompForm)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="comp-entry-button"><Gift className="w-3.5 h-3.5 mr-1.5" />Comp</Button>
          <Button onClick={handleDuplicate} disabled={duplicating} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="duplicate-event-button">{duplicating ? <span className="w-3 h-3 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mr-1.5" /> : <CopyPlus className="w-3.5 h-3.5 mr-1.5" />}Duplicate</Button>
          <Button onClick={() => window.open(`/e/${id}`, '_blank')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="view-event-button"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Public</Button>
          <Button onClick={copyEventLink} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="copy-link-button"><Copy className="w-3.5 h-3.5 mr-1.5" />Link</Button>
          <Button onClick={() => navigate(`/events/${id}/edit`)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="edit-event-button"><Eye className="w-3.5 h-3.5 mr-1.5" />Edit</Button>
          <Button onClick={() => navigate('/guide')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="guide-nav-button"><BookOpen className="w-3.5 h-3.5 mr-1.5" />Guide</Button>
        </div>

        {/* ─── COMP ENTRY FORM ─── */}
        {showCompForm && (
          <div className="bg-[#0A0A0A] border-2 border-[#D4AF37]/30 rounded-sm p-6 mb-6" data-testid="comp-entry-form">
            <h2 className="text-lg font-semibold text-[#EAE7E1] mb-4 flex items-center"><Gift className="w-5 h-5 mr-2 text-[#D4AF37]" />Issue Comp Pass</h2>
            <form onSubmit={handleIssueComp} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Guest Name *</Label><Input value={compForm.guest_name} onChange={(e) => setCompForm({...compForm, guest_name: e.target.value})} required className="mt-1" data-testid="comp-guest-name" /></div>
              <div><Label>Guest Email *</Label><Input type="email" value={compForm.guest_email} onChange={(e) => setCompForm({...compForm, guest_email: e.target.value})} required className="mt-1" data-testid="comp-guest-email" /></div>
              {event.pass_tiers?.length > 0 && <div><Label>Pass Type</Label><select value={compForm.pass_tier_id} onChange={(e) => { const tid = e.target.value; const t = event.pass_tiers.find(x=>x.id===tid); setCompForm({...compForm, pass_tier_id: tid, attendees_count: t ? t.attendees_per_pass : compForm.attendees_count}); }} className="w-full mt-1 px-3 py-2 rounded-sm" data-testid="comp-pass-tier"><option value="">No tier</option>{event.pass_tiers.filter(t => (t.type || 'entry') === 'entry').map(t => <option key={t.id} value={t.id}>{t.tier_name} ({t.attendees_per_pass} pax)</option>)}</select></div>}
              <div><Label>Attendees</Label><Input type="number" min="1" value={compForm.attendees_count} onChange={(e) => setCompForm({...compForm, attendees_count: e.target.value})} className="mt-1" data-testid="comp-attendees" /></div>
              <div className="col-span-2"><Label>Internal Note</Label><Textarea value={compForm.comp_note} onChange={(e) => setCompForm({...compForm, comp_note: e.target.value})} placeholder="VIP guest, press, sponsor..." rows={2} className="mt-1" data-testid="comp-note" /></div>
              <div className="col-span-2 flex space-x-3">
                <Button type="submit" disabled={issuingComp} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm px-6 py-2 font-bold" data-testid="issue-comp-button"><UserPlus className="w-4 h-4 mr-2" />{issuingComp ? 'Issuing...' : 'Issue Comp'}</Button>
                <Button type="button" onClick={() => setShowCompForm(false)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm">Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* ═══ ITER126 PHASE TABS ═══
            Reorganizes existing panels (no new logic, no new endpoints) into
            three phase-aware tabs. Default tab is auto-selected from
            eventStatus; manual override persists for the session via
            `activeTab` state. Sticky on mobile so the host always knows
            which phase view they are in while scrolling long lists. */}
        <div
          className="sticky top-0 z-30 -mx-4 px-4 sm:mx-0 sm:px-0 sm:static bg-[#050505]/95 backdrop-blur-sm border-b border-[#1A1A1A] sm:border-b-0 sm:bg-transparent mb-5"
          data-testid="manage-event-phase-tabs"
          role="tablist"
        >
          <div className="flex gap-1 sm:gap-2 py-2 sm:py-0 sm:mb-2">
            {[
              ['setup', 'Pre-Event', 'approvals · staffing · guests'],
              ['live', 'During Event', 'arrivals · check-in · door'],
              ['wrap', 'Post Event', 'reports · refunds · insights'],
            ].map(([key, label, sub]) => {
              const isActive = currentTab === key;
              const isPhaseDefault = defaultTabForStatus === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(key)}
                  data-testid={`phase-tab-${key}${isActive ? '-active' : ''}`}
                  className={`flex-1 sm:flex-initial sm:min-w-[120px] px-3 sm:px-4 py-2 rounded-sm border transition-colors text-left ${
                    isActive
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#F2D27A]'
                      : 'border-[#1A1A1A] bg-[#0A0A0A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:border-[#333]'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
                    {isPhaseDefault && !isActive && (
                      <span className="text-[8px] font-bold text-[#D4AF37]/70 uppercase tracking-wider">·now</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#777] mt-0.5 hidden sm:block">{sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ SETUP TAB — before-event surfaces ═══
            Approvals, comp guests, discount codes, staff, messaging compose. */}
        {currentTab === 'setup' && (
          <div className="space-y-6" data-testid="phase-tab-content-setup">
            <PendingApprovals
              pendingTickets={actionablePendingTickets}
              selectedTickets={selectedTickets}
              toggleTicketSelection={toggleTicketSelection}
              toggleSelectAll={toggleSelectAll}
              handleBulkApprove={handleBulkApprove}
              approvingBulk={approvingBulk}
            />
            <DiscountCodesPanel eventId={id} event={event} discountCodes={discountCodes} onRefresh={fetchAll} />
            <div ref={compGuestRef}>
              <CompGuestSection eventId={id} compGuests={compGuests} tickets={tickets} event={event} user={user} onRefresh={fetchAll} />
            </div>
            <StaffPanel eventId={id} staff={staff} savedProfiles={savedProfiles} onRefresh={fetchAll} />
            <MessagingPanel eventId={id} event={event} messages={messages} onRefresh={fetchAll} />
          </div>
        )}

        {/* ═══ LIVE TAB — during-event door operations ═══
            AttendeeList carries the operational actions: scan/check-in,
            mark cash collected, refund, revoke/replace. MessagingPanel is
            the broadcast surface during the event. */}
        {currentTab === 'live' && (
          <div className="space-y-6" data-testid="phase-tab-content-live">
            <AttendeeList
              tickets={tickets}
              event={event}
              onScan={handleScanTicket}
              onRevoke={handleRevokeTicket}
              onRevokeAndReplace={(t) => setReplaceModal(t)}
              onIssueRefund={handleIssueRefund}
              onConfirmPayment={handleConfirmPayment}
            />
            <MessagingPanel eventId={id} event={event} messages={messages} onRefresh={fetchAll} />
          </div>
        )}

        {/* ═══ WRAP TAB — after-event analytics + reconciliation ═══
            AttendeeList re-rendered here intentionally — it is the only
            interface to refund actions, which are a core post-event task.
            This is the documented exception to "no panel duplication". */}
        {currentTab === 'wrap' && (
          <div className="space-y-6" data-testid="phase-tab-content-wrap">
            <AnalyticsPanel availability={availability} />
            <InsightsSnapshotCard snapshot={event?.insights_snapshot} />
            <ActivityFeedCard eventId={id} limit={10} title="Event Activity" />
            <AttendeeList
              tickets={tickets}
              event={event}
              onScan={handleScanTicket}
              onRevoke={handleRevokeTicket}
              onRevokeAndReplace={(t) => setReplaceModal(t)}
              onIssueRefund={handleIssueRefund}
              onConfirmPayment={handleConfirmPayment}
            />
          </div>
        )}
      </div>

      {/* Revoke & Replace Confirmation Modal */}
      {replaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" onClick={() => !replacing && setReplaceModal(null)} data-testid="revoke-replace-modal">
          <div className="bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#EAE7E1] mb-1">Revoke & Replace</h3>
            <p className="text-sm text-[#AFAFAF] mb-5">This will revoke the original pass and issue a new one to the same guest in one step.</p>
            <div className="space-y-2 mb-5 text-sm">
              <div className="flex justify-between"><span className="text-[#777]">Guest</span><span className="text-[#EAE7E1] font-semibold">{replaceModal.guest_display_name || replaceModal.guest_name}</span></div>
              <div className="flex justify-between"><span className="text-[#777]">Email</span><span className="text-[#EAE7E1] text-xs">{replaceModal.guest_email}</span></div>
              <div className="flex justify-between"><span className="text-[#777]">Original Pass</span><span className="text-[#EAE7E1] font-mono text-xs">{replaceModal.ticket_number}</span></div>
              <div className="flex justify-between"><span className="text-[#777]">Pass Tier</span><span className="text-[#EAE7E1]">{replaceModal.pass_tier_name || 'General'}{replaceModal.is_comp && <span className="ml-1 text-[10px] text-[#D4AF37] font-bold">COMP</span>}</span></div>
              <div className="flex justify-between"><span className="text-[#777]">Attendees</span><span className="text-[#EAE7E1]">{replaceModal.attendees_count || 1}</span></div>
            </div>
            <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/5 rounded-sm p-3 mb-5 text-xs text-[#B9B1A6] leading-relaxed">
              <div className="font-semibold text-[#D4AF37] mb-1">What happens:</div>
              <ul className="space-y-0.5 list-disc list-inside">
                <li>Original pass becomes <span className="text-[#C44545] font-semibold">revoked</span></li>
                <li>New pass is issued with the same tier, attendees, and payment metadata</li>
                <li>Guest receives ONE email with the new pass + PDF</li>
                <li>Capacity is not re-charged</li>
              </ul>
            </div>
            <div className="flex space-x-3">
              <Button onClick={() => setReplaceModal(null)} variant="outline" disabled={replacing} className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid="revoke-replace-modal-cancel">Cancel</Button>
              <Button onClick={handleRevokeAndReplace} disabled={replacing} className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="revoke-replace-modal-confirm">
                {replacing ? 'Processing...' : 'Revoke & Replace'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
